import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentThread } from '../../../../../api/model';

type SearchType = 'AND' | 'OR';
type SearchStatus = 'default' | 'searching';
interface SearchData {
  searchKeyword: string;
  searchType: SearchType;
  searchDateFrom: string;
  searchDateTo: string;
}
interface ThreadData {
  textElement: Element | null;
  commentElement: Element | null;
  top: number;
  threadId: string;
}
interface Props {
  editor: ClassicEditor;
}

export default forwardRef((props: Props, ref) => {
  // Common
  const { editor } = props;
  const { t } = useTranslation();
  useEffect(() => {
    focusOn('keyword');
  }, []);

  // Data
  const defaultSearchData: SearchData = {
    searchKeyword: '',
    searchType: 'AND',
    searchDateFrom: '',
    searchDateTo: '',
  };
  const [searchKeyword, setSearchKeyword] = useState<string>(defaultSearchData.searchKeyword); // 検索キーワード
  const [searchType, setSearchType] = useState<SearchType>(defaultSearchData.searchType); // 検索タイプ（AND/OR）
  const [searchDateFrom, setSearchDateFrom] = useState<string>(defaultSearchData.searchDateFrom); // 日付（From）
  const [searchDateTo, setSearchDateTo] = useState<string>(defaultSearchData.searchDateTo); // 日付（To）
  const [threadCount, setThreadCount] = useState<number>(-1); // コメント検索結果件数
  const filteredCommentThreadIdsRef = useRef<string[]>([]); // コメント検索結果のスレッドID
  const commentSearchIndexRef = useRef<number>(0); // コメント検索結果のインデックス
  const lastSearchKeywordRef = useRef<string>(''); // 最後に検索したキーワード

  // Search
  const [searchStatus, setSearchStatus] = useState<SearchStatus>('default');
  const handleSearch = () => {
    if (searchKeyword !== '' || searchDateFrom !== '' || searchDateTo !== '') {
      const threads: CommentThread[] = getCommentThreads();
      const filteredThreads: CommentThread[] = getFilteredCommentThreads(threads);
      const filteredThreadCount: number = filteredThreads.length;
      const threadIds: string[] = filteredThreads.map((thread: CommentThread) => _.get(thread, 'id', ''));
      setThreadCount(filteredThreadCount);
      toggleCommentThread(threadIds);
      commentSearchIndexRef.current = 0;
      filteredCommentThreadIdsRef.current = threadIds;
      scrollToTargetThread(threadIds);
      lastSearchKeywordRef.current = searchKeyword;
      setSearchStatus('searching');
    } else {
      handleSearchClear();
    }
  };
  const handleSearchClear = () => {
    setSearchKeyword(defaultSearchData.searchKeyword);
    setSearchType(defaultSearchData.searchType);
    setSearchDateFrom(defaultSearchData.searchDateFrom);
    setSearchDateTo(defaultSearchData.searchDateTo);
    focusOn('keyword');
    toggleCommentThread(null);
    setThreadCount(-1);
    lastSearchKeywordRef.current = '';
    setSearchStatus('default');
  };
  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  const getCommentThreads = () => {
    const commentsRepository = editor.plugins.get('CommentsRepository');
    // @ts-ignore
    return commentsRepository.getCommentThreads(); // eslint-disable-line
  };
  const getCommentKeywords = (keyword: string) => keyword.replace(/\u3000/g, ' ').split(' '); // 全角スペースを半角スペースに変換
  const getCommentText = (thread: CommentThread) => {
    const contents: string[] = [];
    const parser = new DOMParser();
    const commentItems: Comment[] = _.get(thread, 'comments._items', []);
    commentItems.forEach((comment: Comment) => {
      const originalContent: string = _.get(comment, 'content', '');
      const document = parser.parseFromString(originalContent, 'text/html');
      const newContents = Array.from(document.querySelectorAll('p'))
        .map((p) => p.textContent)
        .filter((text) => text !== null);
      newContents.forEach((newContent) => {
        if (newContent) {
          contents.push(newContent);
        }
      });
    });
    return contents.join('\n');
  };
  const getCommentDates = (thread: CommentThread) => {
    const commentItems = _.get(thread, 'comments._items', []);
    const dates = commentItems.map((comment: Comment) => {
      const date = _.get(comment, 'createdAt', '');
      return moment(date);
    });
    return dates;
  };
  const getCommentDateType = () => {
    if (searchDateFrom && searchDateTo) {
      return 'between';
    }
    if (searchDateFrom) {
      return 'greater';
    }
    if (searchDateTo) {
      return 'less';
    }
    return 'none';
  };
  const getFilteredCommentThreads = (threads: CommentThread[]) =>
    threads.filter((thread: CommentThread) => {
      // 要素の存在チェック（コメントスレッドを消さず、文章や画像を消している場合の対応）
      /* eslint-disable */
      const commentThreadId = _.get(thread, 'id', '');
      if (commentThreadId) {
        const threadElement = document.querySelector(`.ck-comment-marker[data-comment="${commentThreadId}"]`); // テキスト&画像で共通
        if (!threadElement) {
          return false;
        }
      }
      /* eslint-enable */
      const hasDate = hasCommentDate(thread); // 日付検索
      if (hasDate === true) {
        return hasCommentText(thread); // テキスト検索
      }
      return false;
    });
  const hasCommentText = (thread: CommentThread) => {
    if (searchKeyword === '') {
      return true; // 検索キーワードが空の場合は全て表示
    }
    const keywords = getCommentKeywords(searchKeyword);
    const text = getCommentText(thread);
    if (searchType === 'AND') {
      return keywords.every((keyword) => text.includes(keyword));
    }
    if (searchType === 'OR') {
      return keywords.some((keyword) => text.includes(keyword));
    }
    return false;
  };
  const hasCommentDate = (thread: CommentThread) => {
    let hasDate = false;
    const fromDt = moment(searchDateFrom).startOf('day');
    const toDt = moment(searchDateTo).endOf('day');
    const dates = getCommentDates(thread);
    const dateType = getCommentDateType();
    if (dateType === 'between') {
      hasDate = dates.some(
        (date: Moment) => date.isBetween(fromDt, toDt, undefined, '[]') // 指定期間のコメントがあるか
      );
    } else if (dateType === 'greater') {
      hasDate = dates.some((date: Moment) => date.isSameOrAfter(fromDt)); // 指定日以降のコメントがあるか
    } else if (dateType === 'less') {
      hasDate = dates.some((date: Moment) => date.isSameOrBefore(toDt)); // 指定日以前のコメントがあるか
    } else if (dateType === 'none') {
      hasDate = true;
    }
    return hasDate;
  };
  const toggleCommentThread = (showingThreadIds: string[] | null) => {
    // showingThreadIdsがnullの場合は全て表示
    const threads: CommentThread[] = getCommentThreads();
    threads.forEach((thread: CommentThread) => {
      const threadId = _.get(thread, 'id', '');
      const threadElement = document.querySelector(`.ck-thread[data-thread-id="${threadId}"]`);
      if (threadElement) {
        const threadContainerElement = threadElement.closest('.ck-sidebar-item');
        if (threadContainerElement) {
          const shouldShow = showingThreadIds === null ? true : showingThreadIds.includes(threadId);
          if (shouldShow === true) {
            threadContainerElement.classList.remove('comment-search-disabled');
          } else {
            threadContainerElement.classList.add('comment-search-disabled');
          }
        }
      }
    });
  };
  const focusOn = (key: string) => {
    if (key === 'keyword') {
      document.getElementById('comment-search-keyword')?.focus();
    } else if (key === 'dateTo') {
      document.getElementById('comment-search-date-to')?.focus();
    }
  };
  const scrollToTargetThread = (threadIds: string[]) => {
    if (threadIds.length > 0) {
      const threadData: ThreadData[] = [];
      threadIds.forEach((threadId: string) => {
        const textElement = document.querySelector(`.ck-comment-marker[data-comment="${threadId}"]`);
        const commentElement = document.querySelector(`.ck-thread[data-thread-id="${threadId}"]`);
        if (textElement && textElement.parentElement) {
          const threadContainerElement = textElement.parentElement;
          const newTop = threadContainerElement.getBoundingClientRect().top;
          threadData.push({
            textElement,
            commentElement,
            top: newTop,
            threadId,
          });
        }
      });
    }
  };

  // 親側へ関数を公開
  useImperativeHandle(ref, () => ({
    handleSearchClear,
  }));

  return (
    <div>
      {/* 検索条件 */}
      <table id="comment-search-table" data-status={searchStatus}>
        <tbody>
          <tr>
            <td>{t('キーワード')}</td>
            <td style={{ width: '100%' }}>
              <input
                type="text"
                id="comment-search-keyword"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                // Enterキーで検索
                onKeyDown={handleKeywordKeyDown}
              />
            </td>
          </tr>
          <tr>
            <td>&nbsp;</td>
            <td className="flex" style={{ paddingTop: '1px' }}>
              <label>
                <input
                  type="radio"
                  name="search_type"
                  value="AND"
                  checked={searchType === 'AND'}
                  onChange={() => setSearchType('AND')}
                />
                AND
              </label>
              &nbsp;
              <label>
                <input
                  type="radio"
                  name="search_type"
                  value="OR"
                  checked={searchType === 'OR'}
                  onChange={() => setSearchType('OR')}
                />
                OR
              </label>
            </td>
          </tr>
          <tr>
            <td>{t('日付: From')}</td>
            <td>
              <input
                type="date"
                value={searchDateFrom}
                onChange={(e) => setSearchDateFrom(e.target.value)}
                onKeyDown={handleDateKeyDown}
              />
            </td>
          </tr>
          <tr>
            <td>{t('日付: To')}</td>
            <td>
              <input
                id="comment-search-date-to"
                type="date"
                value={searchDateTo}
                onChange={(e) => setSearchDateTo(e.target.value)}
                onKeyDown={handleDateKeyDown}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="controlfr" style={{ padding: '0 9px', marginTop: '21px' }}>
        <button type="button" className="cancelbtn" onClick={handleSearchClear}>
          {t('クリア')}
        </button>
        <span>
          <input
            type="submit"
            style={{ marginLeft: '50px' }}
            value={t('検索').toString()}
            className="searchbtn"
            onClick={handleSearch}
          />
        </span>
      </div>
      <div id="comment-search-fotter">
        {(threadCount && threadCount > 0 && (
          <div className="green01">{t('{{count}}件該当しました', { count: threadCount })}</div>
        )) ||
          (threadCount === 0 && <div className="red01">{t('見つかりませんでした')}</div>) ||
          (threadCount === -1 && <>{t('検索してください')}</>)}
      </div>
    </div>
  );
});
